import { GenericDropdownOption } from 'App.types';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { ValueType } from 'react-select';
import useSelectProps from 'Hooks/useSelectProps';
import { useInfiniteQuery } from '@tanstack/react-query';
import { SelectProps } from '@round/ui-kit';

const queryOptions = creatorbaseQueries.billingEntities.options.infinite;

export type BillingEntityOption = GenericDropdownOption<number> & creatorbase.BillingEntity;

export const mapBillingEntityToOption = (entity: creatorbase.BillingEntity): BillingEntityOption => ({
    value: entity.id,
    label: entity.name,
    ...entity,
});

type Params = {
    initialValue?: ValueType<BillingEntityOption, false>;
    params?: Omit<Parameters<typeof queryOptions>[0], 'search' | 'page_size'>;
    enabled?: boolean;
};

type PropGetter = () => SelectProps<BillingEntityOption>;

type Return = { getProps: PropGetter };

export default function useBillingEntitySelect({ initialValue, params, enabled = true }: Params = {}): Return {
    const { debouncedSearchValue, getProps } = useSelectProps<BillingEntityOption, false>({ initialValue });

    const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery({
        ...queryOptions({ ...params, search: debouncedSearchValue, page_size: 25 }),
        enabled,
    });

    const options: BillingEntityOption[] =
        data?.pages.flatMap((page) => {
            return page.results.map(mapBillingEntityToOption);
        }) ?? [];

    return {
        getProps: () =>
            getProps({
                options,
                noOptionsMessage: () => 'No billing entities found',
                isLoading: isFetching,
                onMenuScrollToBottom: () => {
                    if (hasNextPage && !isFetching) {
                        fetchNextPage();
                    }
                },
                filterOption: null,
            }),
    };
}
